import { useEffect } from 'react';

import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'hooks';

import ValidationMessage from 'components/validation/ValidationMessage';

import SharedTextFieldInput from 'components/fields/shared/TextFieldInput';
import ValidateFile from 'components/fields/shared/Message/components/ValidateFile';
import ValidateText from 'components/fields/shared/Message/components/ValidateText';

import useFormBinderDisabled from 'hooks/useFormBinderDisabled';

import { FILE_TYPE } from 'variables/constants';

import {
  addRemark,
  updateRemark,
  removeRemark,
  removeFile,
  removeMissingMandatoryField,
} from 'reducers/autoPersonalFormReducer';

import Validation006Resolver from './Validation006Resolver';
import { useMarketApprovalResolver } from './hooks';
import { getHTMLId, getFieldId, useResolverModal } from './resolverUtils';
import type { ValidationResolverProps } from './types';

export interface Validation133bResolverProps extends ValidationResolverProps {}

const VALIDATION_ID = '133b';
const FORM_STATE_KEY = 'autoPersonalForm';

const DISCLOSURE_TEXT =
  'I confirm that the licensing dates shown on my application represent the ' +
  'original date(s) I was first licensed to drive in Ontario, as recorded by ' +
  'the Ministry of Transportation (MTO). I understand that inaccurate or ' +
  'incomplete disclosure of my licensing history may impact rating, ' +
  'eligibility, or coverage.';

const Validation133bResolver = ({
  section,
  itemKey: propItemKey,
  compact = false,
}: Validation133bResolverProps) => {
  const { uuid } = useParams();
  const isBinderDisabled = useFormBinderDisabled(FORM_STATE_KEY);
  const {
    isOpen: isCompactOpen,
    onOpen: onCompactOpen,
    onClose: onCompactClose,
  } = useDisclosure();

  const itemKey = propItemKey || 'global';

  const { state, handlers, fieldIds, alertMessage } = useMarketApprovalResolver(
    {
      validationId: VALIDATION_ID,
      section,
      itemKey,
      formStateKey: FORM_STATE_KEY,
      showLOEOption: true,
      marketApprovalFileType: FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO.type,
      loeFileType: FILE_TYPE.TYPE_133b.type,
    },
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (state.remark?.custom_data?.type === 'market_approval') {
      dispatch(
        updateRemark({
          validation_key: VALIDATION_ID,
          item_key: itemKey,
          data: { custom_data: null },
        }),
      );
    }
  }, [state.remark, dispatch, itemKey]);

  // Caution B applies only once the AI has rejected the broker's explanation.
  const showCautionB = state.remark?.status === 'failed';

  const resolutionItemKey = `${itemKey}-resolution`;
  const marketItemKey = `${resolutionItemKey}-market`;
  const {
    state: resolutionState,
    handlers: resolutionHandlers,
    fieldIds: resolutionFieldIds,
  } = useMarketApprovalResolver({
    validationId: VALIDATION_ID,
    section,
    itemKey: resolutionItemKey,
    formStateKey: FORM_STATE_KEY,
    showLOEOption: true,
    marketApprovalFileType: FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO.type,
    loeFileType: FILE_TYPE.TYPE_133b.type,
    forceShow: showCautionB,
    customAlertMessage: showCautionB ? alertMessage : '',
    remarksRequired: false,
    resolutionOnly: true,
  });

  const marketRemark = useAppSelector((s) =>
    s[FORM_STATE_KEY].remarks.find(
      (r: { validation_key: string; item_key: string }) =>
        r.validation_key === VALIDATION_ID && r.item_key === marketItemKey,
    ),
  );
  const marketFile = useAppSelector((s) =>
    s[FORM_STATE_KEY].files.find(
      (f: { validation_key: string; item_key: string }) =>
        f.validation_key === VALIDATION_ID && f.item_key === marketItemKey,
    ),
  );

  const isLoeComplete =
    resolutionState.resolutionType === 'loe' &&
    !!resolutionState.file &&
    (resolutionState.file.status === 'approved' ||
      !!resolutionState.file.is_deferred);

  const isMarketApprovalComplete = (() => {
    if (resolutionState.resolutionType !== 'market_approval') return false;
    const cd =
      marketRemark?.custom_data?.type === 'market_approval'
        ? marketRemark.custom_data
        : null;
    if (!cd?.approval_method) return false;
    if (!cd.approval_date || !cd.approval_time) return false;
    if (!cd.underwriter_name?.trim() || !cd.market?.trim()) return false;
    if (cd.approval_method === 'email') {
      return (
        !!marketFile &&
        (marketFile.status === 'approved' || !!marketFile.is_deferred)
      );
    }
    return !!cd.summary_of_market_conversation_text?.trim();
  })();

  const isResolutionComplete = isLoeComplete || isMarketApprovalComplete;

  useEffect(() => {
    if (resolutionState.resolutionType === 'market_approval') return;

    dispatch(
      removeRemark({
        validation_key: VALIDATION_ID,
        item_key: marketItemKey,
      }),
    );
    dispatch(
      removeFile({
        validation_key: VALIDATION_ID,
        item_key: marketItemKey,
      }),
    );
    [
      'Market Approval Resolution',
      'Market Approval Method',
      'Market Approval Remarks',
      'Market Approval Date',
      'Market Approval Time',
      'Market Approval Underwriter',
      'Market Approval Market',
    ].forEach((name) => {
      dispatch(removeMissingMandatoryField(getFieldId(name, marketItemKey)));
    });
  }, [resolutionState.resolutionType, marketItemKey, dispatch]);

  useEffect(() => {
    if (!showCautionB || !isResolutionComplete) return;

    if (resolutionState.remark?.status !== 'approved') {
      dispatch(
        updateRemark({
          validation_key: VALIDATION_ID,
          item_key: resolutionItemKey,
          data: { status: 'approved' },
        }),
      );
    }

    if (
      isMarketApprovalComplete &&
      marketRemark &&
      marketRemark.status !== 'approved'
    ) {
      dispatch(
        updateRemark({
          validation_key: VALIDATION_ID,
          item_key: marketItemKey,
          data: { status: 'approved' },
        }),
      );
    }
  }, [
    showCautionB,
    isResolutionComplete,
    isMarketApprovalComplete,
    resolutionState.remark,
    marketRemark,
    resolutionItemKey,
    marketItemKey,
    dispatch,
  ]);

  const get133bCompactAlertStatus = () => {
    if (state.remark?.status === 'approved') return 'success';
    if (state.remark?.status === 'failed' && isResolutionComplete)
      return 'success';
    return 'warning';
  };

  const disclosureItemKey = `${itemKey}-disclosure`;
  const disclosureRemark = useAppSelector((s) =>
    s[FORM_STATE_KEY].remarks.find(
      (r: { validation_key: string; item_key: string }) =>
        r.validation_key === VALIDATION_ID && r.item_key === disclosureItemKey,
    ),
  );

  useEffect(() => {
    if (state.show && !disclosureRemark) {
      dispatch(
        addRemark({
          validation_key: VALIDATION_ID,
          item_key: disclosureItemKey,
          text: DISCLOSURE_TEXT,
          status: 'approved',
          text_validator_log_id: null,
          ai_result: '',
          section,
        }),
      );
    }
  }, [state.show, disclosureRemark, dispatch, disclosureItemKey, section]);

  const htmlId = getHTMLId(VALIDATION_ID, itemKey);
  // Missing-file links target the LOE upload's own item key
  const resolutionHtmlId = getHTMLId(VALIDATION_ID, resolutionItemKey);

  // Use shared modal hook for hash-based navigation
  const { handleModalClose } = useResolverModal(
    compact,
    [fieldIds.remarks, resolutionFieldIds.resolution, resolutionHtmlId],
    htmlId,
    onCompactOpen,
    onCompactClose,
  );

  if (!state.show) return null;

  // Compact mode: show button that opens modal
  if (compact) {
    return (
      <>
        {/* Anchor divs for mandatory field navigation */}
        <div id={htmlId}></div>
        <div id={resolutionHtmlId}></div>
        <div id={fieldIds.remarks}></div>
        <div id={resolutionFieldIds.resolution}></div>
        <Tooltip placement="top" label={alertMessage}>
          <Alert status={get133bCompactAlertStatus()} mt="10px" fontSize="14px">
            <Button
              onClick={() => onCompactOpen()}
              w="100%"
              whiteSpace="normal"
            >
              <AlertIcon />
              Add remark
            </Button>
          </Alert>
        </Tooltip>
        <Modal
          isOpen={isCompactOpen}
          onClose={handleModalClose}
          size="4xl"
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              License and Insurance Experience Resolution
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box mb="10px">
                <Validation133bResolver
                  section={section}
                  itemKey={itemKey}
                  compact={false}
                />
              </Box>
              <Button
                variant="brand"
                onClick={handleModalClose}
                mb="15px"
                float="right"
              >
                Close
              </Button>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <Box>
      {/* Anchor divs for mandatory field navigation */}
      <div id={htmlId}></div>
      <div id={fieldIds.remarks}></div>
      <div id={resolutionFieldIds.resolution}></div>
      <Alert status="warning" mt="10px" fontSize="14px">
        <AlertIcon />
        <ValidationMessage message={alertMessage} />
      </Alert>

      <Box mt="15px" p="15px" bg="gray.50" borderRadius="md">
        {/* Mandatory remarks with AI validation */}
        {state.remark && (
          <VStack spacing={4} align="stretch" mb="15px">
            <Box id={fieldIds.remarks}>
              <SharedTextFieldInput
                label="Remarks (reasonable explanation for the discrepancy)"
                value={state.remark.text || ''}
                onChange={handlers.handleRemarksChange}
                borderColor={
                  !state.remark.text || state.remark.text.trim().length === 0
                    ? 'red'
                    : 'inherit'
                }
                formStateKey={FORM_STATE_KEY}
                section={section}
              />
            </Box>
            <ValidateText
              uuid={uuid}
              remark={state.remark}
              validationId={VALIDATION_ID}
              itemKey={itemKey}
              formStateKey={FORM_STATE_KEY}
              context={alertMessage}
            />
          </VStack>
        )}

        {/* Caution B — only after AI rejects the broker remarks */}
        {showCautionB && (
          <>
            <Alert status="warning" mb="15px" fontSize="14px">
              <AlertIcon />
              <Box>
                Additional information is required due to the discrepancy
                identified within the driver's licensing and/or insurance
                history. One of the following actions is mandatory prior to
                proceeding: upload supporting documentation, including Driver's
                License History (DLH), Letter of Experience (LOE), or both; OR
                obtain Market Approval. Please ensure all supporting rationale
                and documentation are accurately recorded in the remarks.
              </Box>
            </Alert>
            <FormControl mb="15px" id={resolutionFieldIds.resolution}>
              <FormLabel fontWeight="semibold">
                Please resolve using one of the following:
              </FormLabel>
              <RadioGroup
                value={resolutionState.resolutionType || ''}
                onChange={resolutionHandlers.handleResolutionTypeChange}
                isDisabled={isBinderDisabled}
              >
                <Stack direction="column" spacing={2}>
                  <Radio value="loe">
                    Upload supporting documentation (DLH, LOE, or both)
                  </Radio>
                  <Radio value="market_approval">Obtain Market Approval</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            {/* Supporting documentation path */}
            {resolutionState.resolutionType === 'loe' &&
              resolutionState.file && (
                <ValidateFile
                  uuid={uuid}
                  file={resolutionState.file}
                  validationId={VALIDATION_ID}
                  itemKey={resolutionItemKey}
                  formStateKey={FORM_STATE_KEY}
                />
              )}

            {resolutionState.resolutionType === 'market_approval' && (
              <Validation006Resolver
                section={section}
                itemKey={`${resolutionItemKey}-market`}
                validationId={VALIDATION_ID}
                marketApprovalFileType={
                  FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO.type
                }
                modalTitle="License and Insurance Experience – Market Approval"
                forceShow
                customAlertMessage={alertMessage}
                hideAlertMessage
                validationTypeKey="remarks_market_approval"
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Validation133bResolver;
